import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import chevronLeft from 'assets/icons/chevron-left.svg';
import { SelectInput, TextInput } from 'components/Inputs';
import Button from 'components/Button';
import getStripe from 'utils/stripe';
import { get, getUserIdFromToken, post } from 'api';
import { Levels, MenLevels, WomenLevels } from 'constants/Selections';
import { checkRegistrationPoints } from 'utils/tournamentHelper';
import { SuccessToast, ErrorToast } from 'utils/Toasts';
import SpinningBox from 'components/SpinningBox';

const Registration = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const [profile, setProfile] = useState({});
  const [open, setOpen] = useState({});
  const [tournaments, setTournaments] = useState([]);
  const [form, setForm] = useState({
    userId: '',
    firstName: '',
    lastName: '',
    email: '',
    tournament: '',
    gender: '',
    level: '',
  });
  const formData = JSON.parse(localStorage.getItem('formData'));
  const registrationPoints = localStorage.getItem('registrationPoints');
  const [isNewCustomer, setIsNewCustomer] = useState(null);
  const [levels, setLevels] = useState({ Moterys: [], Vyrai: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userId = getUserIdFromToken();

  const checkAlreadyRegistered = async ({
    tournamentId,
    userId,
    gender,
    level,
  }) => {
    try {
      const response = await get(
        `/tournaments/check-registration/${tournamentId}/${userId}/${gender}/${level}`,
      );
      const data = await response.json();
      return data.registered;
    } catch (err) {
      console.error('Failed to check registration status', err);
      return false;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (sessionId && form.userId && form.tournament) {
      setIsSubmitting(true);

      const pollRegistration = async () => {
        let attempts = 0;
        const maxAttempts = 10;

        const interval = setInterval(async () => {
          attempts++;
          const isRegistered = await checkAlreadyRegistered({
            tournamentId: form.tournament,
            userId: form.userId,
            gender: form.gender,
            level: form.level,
          });

          if (isRegistered || attempts >= maxAttempts) {
            clearInterval(interval);

            if (isRegistered) {
              successToastRef.current.showSuccess(t('registrationSuccess'));
              setTimeout(() => {
                setIsSubmitting(false);
                localStorage.removeItem('formData');
                navigate(`/turnyrai/${id}`, {
                  state: { from: 'tournament-registration', data: form },
                });
              }, 3000);
            } else {
              errorToastRef.current.showError(t('registrationNotConfirmed'));
              setIsSubmitting(false);
            }
          }
        }, 3000);
      };

      pollRegistration();
    }
  }, [location.search, form.userId, form.tournament]);

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const response = await get('/tournaments/tournament-levels');
        const data = await response.json();
        const levelsData = data.reduce((acc, level) => {
          if (!acc[level.gender]) {
            acc[level.gender] = [];
          }
          acc[level.gender].push(level);
          return acc;
        }, {});
        setLevels(levelsData);
      } catch (error) {
        console.error('Failed to fetch levels', error);
      }
    };

    fetchLevels();
  }, []);

  useEffect(() => {
    const getUserDetails = async () => {
      const response = await get('/accounts/myprofile');
      const data = await response.json();
      setProfile(data[0]);

      const savedForm = JSON.parse(localStorage.getItem('formData'));
      if (savedForm) {
        setForm({
          ...savedForm,
          userId: data[0].id,
          firstName: data[0].first_name,
          lastName: data[0].last_name,
          email: data[0].email,
        });
      } else {
        setForm({
          ...form,
          userId: data[0].id,
          firstName: data[0].first_name,
          lastName: data[0].last_name,
          email: data[0].email,
          tournament: id ? parseInt(id) : '',
        });
      }
    };
    const getTournaments = async () => {
      const response = await get('/tournaments/reg-open');
      const data = await response.json();

      // Filter tournaments by ID if present in params
      const filteredData = id
        ? data.filter((tournament) => tournament.id === parseInt(id))
        : data;

      const options = filteredData.map((tournament) => ({
        value: tournament.id,
        label: `${tournament.tournament_name} (${tournament.city})`,
      }));
      setTournaments(options);
    };

    getUserDetails();
    getTournaments();
  }, [id]);

  useEffect(() => {
    const getIsNewCustomer = async () => {
      const response = await get(`/tournaments/check-new-customer/${userId}`);
      const data = await response.json();
      setIsNewCustomer(data.isNewCustomer);
    };
    getIsNewCustomer();
  }, [userId]);

  const handleOpen = (id) => {
    setOpen({ ...open, [id]: true });
  };

  const handleClose = (id) => {
    setOpen({ ...open, [id]: false });
  };

  const handleChange = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const getOptions = () => {
    if (!form.tournament) return [];

    const selectedTournament = tournaments.find(
      (tournament) => tournament.value === form.tournament,
    );

    if (!selectedTournament) return [];

    const city = selectedTournament.label
      .split('(')[1]
      .split(')')[0]
      .toLowerCase();

    const gender = Object.keys(levels).find((gender) =>
      form.gender.includes(gender),
    );

    if (!gender) return [];

    const options = levels[gender]
      .filter((level) => level[`${city}On`])
      .map((level) => ({ value: level.value, label: level.label }));

    return options;
  };

  const options = useMemo(() => getOptions(), [form.tournament, form.gender]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const registrationPoints = checkRegistrationPoints(form.level);
    if (!form.level || !form.tournament || !form.gender) {
      errorToastRef.current.showError(t('fillAllFields'));
      setIsSubmitting(false);
      return;
    }

    const isDuplicate = await checkAlreadyRegistered({
      tournamentId: form.tournament,
      userId: form.userId,
      gender: form.gender,
      level: form.level,
    });

    if (isDuplicate) {
      errorToastRef.current.showError(t('alreadyRegistered'));
      setIsSubmitting(false);
      return;
    }

    const formToSend = { ...form, registrationPoints };
    localStorage.setItem('formData', JSON.stringify(formToSend));

    const selectedTournament = tournaments.find(
      (tournament) => tournament.value === form.tournament,
    );

    const isMiniTournament = selectedTournament.label
      .toLowerCase()
      .includes('mini');

    try {
      // Save temp registration
      await post('/tournaments/temp-save', formToSend);

      await new Promise((res) => setTimeout(res, 1000));

      const sessionRes = await post('/tournaments/create-checkout-session', {
        gender: form.gender,
        level: form.level,
        tournament_id: form.tournament,
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        isNewCustomer,
        isMiniTournament,
      });
      const sessionData = await sessionRes.json();

      if (!sessionData.id) {
        errorToastRef.current.showError(t('paymentError'));
        return;
      }

      const stripe = await getStripe();
      await stripe.redirectToCheckout({
        sessionId: sessionData.id,
      });
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error during registration:', error);
      errorToastRef.current.showError(t('paymentError'));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripeCanceled') === 'true') {
      if (errorToastRef.current) {
        errorToastRef.current.showError(t('canceledPayment'));
      }
      localStorage.removeItem('formData');
      setIsSubmitting(false);
      setTimeout(() => {
        navigate(`/turnyras/registracija/${id}`);
      }, 3000);
    }
  }, [location.search, id]);

  async function registerForTournament() {
    const response = await post('/tournaments/registration', {
      tournament_id: formData.tournament,
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      tournament_gender: formData.gender,
      tournament_level: formData.level,
      doubles_partner: '',
      registrationPoints,
    });
    const data = await response.json();
    if (data.error) {
      errorToastRef.current.showError(data.error);
    } else {
      successToastRef.current.showSuccess(t('registrationSuccess'));
      setTimeout(() => {
        window.localStorage.removeItem('registrationPoints');
        navigate(`/turnyrai/${id}`, {
          state: { from: 'tournament-registration', data: formData },
        });
      }, 3000);
    }
  }

  return (
    <Box
      sx={{
        marginBottom: '100px',
        margin: '0 auto',
        maxWidth: '416px',
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <Box
        display={'flex'}
        alignItems={'center'}
        onClick={() => navigate(`/turnyras/${id}`)}
        sx={{ cursor: 'pointer' }}
        marginBottom={'32px'}
      >
        <Box
          component={'img'}
          src={chevronLeft}
          marginRight={'12px'}
          height={1}
          alt="chevron Left"
        />
        <Typography
          variant="body3"
          color={theme.palette.darkGrey[700]}
          sx={{ fontWeight: 600 }}
        >
          {t('goBack')}
        </Typography>
      </Box>
      <Typography
        variant="h5"
        fontWeight={700}
        color={theme.palette.darkGrey[700]}
      >
        {t('joinTournament')}
      </Typography>
      <Typography
        variant="body3"
        color={theme.palette.darkGrey[700]}
        sx={{ marginTop: '12px' }}
      >
        {t('tournamentRegSubtitle')}
      </Typography>
      <Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={t('firstName')}
            id="firstName"
            name="firstName"
            type="text"
            placeholder={t('firstName')}
            disabled
            handleInputChange={handleChange}
            value={form.firstName}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={t('lastName')}
            disabled
            id="lastName"
            name="lastName"
            type="text"
            placeholder={t('lastName')}
            handleInputChange={handleChange}
            value={form.lastName}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={t('email')}
            id="email"
            name="email"
            type="email"
            placeholder={t('email')}
            disabled
            handleInputChange={handleChange}
            value={form.email}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          {id ? (
            <TextInput
              label={t('tournament')}
              id="tournament"
              name="tournament"
              type="text"
              placeholder={t('tournament')}
              disabled
              handleInputChange={handleChange}
              value={
                tournaments.find((t) => t.value === form.tournament)?.label ||
                ''
              }
            />
          ) : (
            <>
              <Typography
                variant="body4"
                fontWeight={500}
                color={theme.palette.darkGrey[700]}
              >
                {t('tournament')}*
              </Typography>
              <SelectInput
                name="tournament"
                id="tournament"
                placeholder={t('selectTournament')}
                open={open}
                handleOpen={handleOpen}
                handleClose={handleClose}
                handleChange={handleChange}
                topMargin={'6px'}
                value={form.tournament}
                options={tournaments}
              />
            </>
          )}
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('gender')}*
          </Typography>
          <SelectInput
            name="gender"
            id="gender"
            placeholder={t('selectGender')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.gender}
            options={[
              {
                value: 'Vyrai',
                label: 'Vyrai',
              },
              {
                value: 'Moterys',
                label: 'Moterys',
              },
            ]}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            NTRP ({t('level')})*
          </Typography>
          <SelectInput
            name="level"
            id="level"
            placeholder={t('selectLevel')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.level}
            options={options}
          />
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          maxWidth={{ xs: '100%', sm: '100%' }}
          width={'100%'}
          buttontype={'orange'}
          disabled={isSubmitting}
          onClick={() => handleSubmit()}
        >
          {isSubmitting ? <SpinningBox /> : t('checkout')}
        </Button>
      </Box>
    </Box>
  );
};

export default Registration;
